package ca.northroot.app.data

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.where
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.map

class FirebaseDb : Db {
    private fun getAuth() = Firebase.auth
    private fun getDb() = Firebase.firestore

    override fun getJobs(companyId: String, date: Long): Flow<List<Job>> {
        if (companyId.isBlank()) return emptyFlow()
        
        return getDb().collection("companies").document(companyId)
            .collection("jobs")
            .snapshots
            .map { snap ->
                snap.documents.map { doc ->
                    try {
                        Job(
                            id = doc.id,
                            customerId = doc.get<String?>("customerId") ?: "",
                            customerName = doc.get<String?>("customerName") ?: "",
                            serviceType = doc.get<String?>("serviceType") ?: "",
                            status = try { JobStatus.valueOf(doc.get<String?>("status") ?: "PENDING") } catch(e: Exception) { JobStatus.PENDING },
                            price = try { doc.get<Double>("price") } catch(e: Exception) { 0.0 },
                            notes = doc.get<String?>("notes") ?: "",
                            assignedTo = try { doc.get<List<String>>("assignedTo") } catch(e: Exception) { emptyList() },
                            assignedNames = try { doc.get<List<String>>("assignedNames") } catch(e: Exception) { emptyList() },
                            isRecurring = try { doc.get<Boolean>("isRecurring") } catch(e: Exception) { false },
                            scheduledDate = doc.getTime("scheduledDate")
                        )
                    } catch (e: Exception) {
                        Job(id = doc.id, customerName = "Mapping Error")
                    }
                }
                .sortedBy { it.scheduledDate ?: 0L }
            }
    }

    override fun getTeamLocations(companyId: String): Flow<List<UserProfile>> {
        if (companyId.isBlank()) return emptyFlow()
        
        return getDb().collection("users")
            .where { "companyId" equalTo companyId }
            .snapshots
            .map { snap ->
                snap.documents.map { doc ->
                    try {
                        UserProfile(
                            uid = doc.id,
                            email = doc.get<String?>("email") ?: "",
                            displayName = doc.get<String?>("displayName") ?: "",
                            role = try { UserRole.valueOf(doc.get<String?>("role") ?: "USER") } catch(e: Exception) { UserRole.USER },
                            companyId = doc.get<String?>("companyId") ?: "",
                            lastLatitude = try { doc.get<Double?>("lastLatitude") } catch(e: Exception) { null },
                            lastLongitude = try { doc.get<Double?>("lastLongitude") } catch(e: Exception) { null },
                            lastLocationUpdate = doc.getTime("lastLocationUpdate")
                        )
                    } catch (e: Exception) {
                        UserProfile(uid = doc.id)
                    }
                }.filter { it.lastLatitude != null }
            }
    }

    override fun getCustomers(companyId: String): Flow<List<Customer>> {
        if (companyId.isBlank()) return emptyFlow()
        
        return getDb().collection("companies").document(companyId)
            .collection("customers")
            .snapshots
            .map { snap ->
                snap.documents.map { doc ->
                    try {
                        Customer(
                            id = doc.id,
                            firstName = doc.get<String?>("firstName") ?: "",
                            lastName = doc.get<String?>("lastName") ?: "",
                            name = doc.get<String?>("name") ?: "",
                            email = doc.get<String?>("email") ?: "",
                            phone = doc.get<String?>("phone") ?: "",
                            address = doc.get<String?>("address") ?: ""
                        )
                    } catch (e: Exception) {
                        Customer(id = doc.id, name = "Mapping Error")
                    }
                }
            }
    }

    override fun getCompany(companyId: String): Flow<Company?> {
        if (companyId.isBlank()) return emptyFlow()
        
        return getDb().collection("companies").document(companyId)
            .snapshots
            .map { doc ->
                if (doc.exists) {
                    Company(
                        id = doc.id,
                        name = doc.get<String?>("name") ?: ""
                    )
                } else null
            }
    }

    override suspend fun updateJobStatus(companyId: String, jobId: String, status: JobStatus) {
        if (companyId.isBlank()) return
        getDb().collection("companies").document(companyId)
            .collection("jobs").document(jobId)
            .update("status" to status.name)
    }
    
    suspend fun signIn(email: String, pass: String): dev.gitlive.firebase.auth.FirebaseUser {
        val result = getAuth().signInWithEmailAndPassword(email, pass)
        return result.user!!
    }

    suspend fun signUp(email: String, pass: String): dev.gitlive.firebase.auth.FirebaseUser {
        val result = getAuth().createUserWithEmailAndPassword(email, pass)
        val user = result.user!!
        try {
            val userRef = getDb().collection("users").document(user.uid)
            if (!userRef.get().exists) {
                userRef.set(
                    mapOf(
                        "uid" to user.uid,
                        "email" to email,
                        "displayName" to email.substringBefore("@"),
                        "role" to "USER",
                        "isActive" to true
                    )
                )
            }
        } catch (_: Exception) { }
        return user
    }

    suspend fun getProfile(uid: String): UserProfile? {
        val doc = getDb().collection("users").document(uid).get()
        return if (doc.exists) {
            UserProfile(
                uid = doc.id,
                email = doc.get<String?>("email") ?: "",
                displayName = doc.get<String?>("displayName") ?: "",
                role = try { UserRole.valueOf(doc.get<String?>("role") ?: "USER") } catch(e: Exception) { UserRole.USER },
                companyId = doc.get<String?>("companyId") ?: "",
                lastLocationUpdate = doc.getTime("lastLocationUpdate")
            )
        } else null
    }

    fun getCurrentUser(): dev.gitlive.firebase.auth.FirebaseUser? {
        return try { getAuth().currentUser } catch(e: Exception) { null }
    }
}
